import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BedDouble, CalendarDays, Car, Flame, Footprints, MapPin, Music, Tent } from "lucide-react";
import { Reveal } from "../components/Reveal";

const PROGRAMME = [
  { icon: BedDouble, title: "Nuit à Marrakech", text: "Arrivée et nuit dans un riad, avec le petit-déjeuner." },
  { icon: Car, title: "Voiturier jusqu'au désert", text: "Un service de voiturier te récupère et t'emmène aux portes du désert." },
  { icon: Footprints, title: "6 jours de marche", text: "5 nuits et 6 jours avec les nomades, 3 à 4 h de marche par jour dans les dunes du Sahara. Fatiguée ? Tu peux louer un dromadaire." },
  { icon: Flame, title: "Repas au feu", text: "Les repas sont prévus, élaborés par nos hôtes, à la lumière du feu et sous les étoiles." },
  { icon: Tent, title: "Nuits berbères", text: "Nuitées sous les tentes berbères — prévois ton sac de couchage, en décembre les nuits sont fraîches." },
  { icon: Music, title: "On se libère", text: "Après la marche et au réveil : on respire au son des tambours, on constelle sous les étoiles, on médite, on chante, on danse." },
];

export default function Sejour() {
  const [videoError, setVideoError] = useState(false);

  return (
    <div data-testid="sejour-page">
      {/* HERO TREK SAHARA */}
      <section className="relative h-[85vh] min-h-[540px] overflow-hidden">
        <img
          src="/images/trek-dunes.jpg"
          alt="Dunes du désert du Sahara au Maroc au coucher du soleil"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="sejour-hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-olive-dark/85 via-olive-dark/30 to-transparent" />
        <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex flex-col justify-end pb-16">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-beige-300 mb-6">Séjour initiatique — prochain départ</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight text-beige-100 leading-[1.05] max-w-3xl">
              Trek dans le désert du Sahara
            </h1>
            <div className="mt-6 flex flex-wrap gap-3 text-sm text-olive-dark">
              <span className="inline-flex items-center gap-2 rounded-full bg-beige-100/90 px-4 py-2" data-testid="trek-dates">
                <CalendarDays className="w-4 h-4" strokeWidth={1.5} /> Du 7 au 14 décembre 2026
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-beige-100/90 px-4 py-2" data-testid="trek-location">
                <MapPin className="w-4 h-4" strokeWidth={1.5} /> Maroc — désert du Sahara
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 font-medium text-olive-dark" data-testid="trek-places">
                Plus que 3 places
              </span>
            </div>
            <p className="mt-6 max-w-xl text-beige-200/90 leading-relaxed">
              Huit jours dans une vie… pour te recentrer, te libérer, respirer — prendre le temps hors du temps, au cœur des dunes du Sahara.
            </p>
            <Link
              to="/trek-sahara"
              data-testid="sejour-cta-hero"
              className="mt-9 inline-flex w-fit items-center gap-2 rounded-full bg-beige-100 px-8 py-4 text-sm font-medium text-olive-dark transition-all duration-300 hover:-translate-y-0.5 hover:bg-beige-50"
            >
              Découvrir la page du trek <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* INTRO POÉTIQUE */}
      <section className="py-20 lg:py-24 px-6 lg:px-12 max-w-4xl mx-auto text-center" data-testid="trek-intro">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-olive mb-8">Un cadeau exceptionnel</p>
          <p className="font-serif text-2xl sm:text-3xl font-light italic text-ink leading-relaxed">
            « Imagine… exporte-toi un instant dans le silence du désert, ferme les yeux. Ressens-tu la chaleur du soleil de décembre sur ton visage ? Le sable sous tes pas ? L'immensité des dunes à perte de vue ? »
          </p>
          <p className="mt-8 text-sage leading-relaxed max-w-2xl mx-auto">
            Un instant suspendu au milieu du tumulte de ta vie. Une véritable pause de 8 jours — 8 jours dans une vie, pour te recentrer, te libérer, respirer, prendre le temps hors du temps.
          </p>
        </Reveal>
      </section>

      {/* VIDÉO DU TREK */}
      <section className="pb-24 lg:pb-28 px-6 lg:px-12 max-w-7xl mx-auto" data-testid="trek-sahara-section">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-olive mb-6">En vidéo</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-ink leading-tight">L'esprit du trek, en images</h2>
            <p className="mt-6 text-sage leading-relaxed max-w-lg">
              Un aperçu des précédentes éditions : la caravane qui s'étire sur les dunes, les bivouacs sous les étoiles, les cercles du soir. Marche méditative, respiration consciente et nuits au grand air — un voyage autant intérieur qu'extérieur.
            </p>
            <div className="mt-8 overflow-hidden rounded-2xl border border-olive-border shadow-[0_20px_40px_-25px_rgba(49,63,21,0.35)] max-w-sm" data-testid="trek-affiche">
              <img src="/images/trek-affiche.jpg" alt="Affiche du programme du trek initiatique au Sahara, du 7 au 14 décembre 2026" className="w-full h-auto" />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="overflow-hidden rounded-3xl border border-olive-border shadow-[0_25px_50px_-25px_rgba(49,63,21,0.35)]" data-testid="trek-media-frame">
              {!videoError ? (
                <video
                  data-testid="trek-video"
                  className="w-full h-[420px] object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                  poster="/images/trek-bivouac.jpg"
                  onError={() => setVideoError(true)}
                >
                  <source src="/api/videos/trek-sahara.webm" type="video/webm" />
                  <source src="/api/videos/trek-sahara.mp4" type="video/mp4" />
                </video>
              ) : (
                <img
                  data-testid="trek-image"
                  src="/images/trek-bivouac.jpg"
                  alt="Bivouac berbère sous les étoiles, lanternes autour du feu"
                  className="w-full h-[420px] object-cover"
                />
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* GALERIE DU TREK */}
      <section className="pb-24 lg:pb-28 px-6 lg:px-12 max-w-7xl mx-auto" data-testid="trek-galerie">
        <Reveal className="mb-12">
          <p className="text-xs uppercase tracking-[0.3em] text-olive mb-4">En images</p>
          <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-ink max-w-2xl">Le désert, comme si tu y étais</h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 gap-6">
          {[
            { src: "/images/trek-feu.jpg", alt: "Veillée autour du feu sous les étoiles du désert", testid: "galerie-feu" },
            { src: "/images/trek-the.jpg", alt: "Mohammed préparant le thé dans le désert", testid: "galerie-the", offset: true },
            { src: "/images/trek-coucher.jpg", alt: "Coucher de soleil sur les dunes, assises dans le sable", testid: "galerie-coucher" },
            { src: "/images/trek-groupe.jpg", alt: "Le groupe rassemblé autour du feu au bivouac, dromadaires au repos", testid: "galerie-groupe", offset: true },
          ].map((g, i) => (
            <Reveal key={g.testid} delay={(i % 2) * 0.12}>
              <div className={`overflow-hidden rounded-3xl border border-olive-border ${g.offset ? "sm:mt-14" : ""}`} data-testid={g.testid}>
                <img src={g.src} alt={g.alt} loading="lazy" className="w-full h-80 sm:h-96 object-cover transition-transform duration-700 hover:scale-105" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* LE DÉROULEMENT */}
      <section className="pb-24 lg:pb-28 px-6 lg:px-12 max-w-7xl mx-auto" data-testid="trek-programme">
        <Reveal className="mb-14">
          <p className="text-xs uppercase tracking-[0.3em] text-olive mb-4">Le déroulement</p>
          <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-ink max-w-2xl">8 jours hors du temps, tout est prévu</h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
          {PROGRAMME.map((p, i) => (
            <Reveal key={p.title} delay={(i % 3) * 0.1}>
              <div className="border-t-2 border-olive pt-6" data-testid={`trek-etape-${i}`}>
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-olive-soft border border-olive-border">
                  <p.icon className="w-5 h-5 text-olive" strokeWidth={1.5} />
                </span>
                <h3 className="mt-5 font-serif text-2xl text-ink">{p.title}</h3>
                <p className="mt-3 text-sm sm:text-base text-sage leading-relaxed">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TARIF + CTA */}
      <section className="bg-olive-dark text-beige-100" data-testid="trek-prix">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-beige-300/80 mb-4">Combien ça coûte ?</p>
            <p className="font-serif text-6xl sm:text-7xl font-light">960 €</p>
            <p className="mt-4 text-beige-300/90 leading-relaxed max-w-md">
              Tout compris : 8 jours, repas, couchage et ateliers. Paiement en plusieurs fois possible — un simple acompte t'est demandé pour réserver. Non inclus : le vol et le 1er repas — il ne te reste qu'à choisir ton vol, selon ta ville de départ.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="lg:justify-self-end">
            <p className="font-serif text-2xl sm:text-3xl font-light leading-snug max-w-md">Alors, tu tentes l'aventure avec Julie, Mohammed et sa famille ?</p>
            <p className="mt-4 inline-flex items-center rounded-full bg-gold px-4 py-2 text-sm font-medium text-olive-dark" data-testid="trek-places-bottom">Plus que 3 places disponibles</p>
            <div>
              <Link to="/trek-sahara" data-testid="sejour-cta-bottom" className="mt-6 inline-flex items-center rounded-full bg-beige-100 px-8 py-4 text-sm font-medium text-olive-dark transition-all duration-300 hover:-translate-y-0.5 hover:bg-beige-50">
                Je tente l'aventure
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
